<template>
	<div v-if="sortedOffersHistory.length > 0" class="soh" v-observe-visibility="onVisibleCallback">
		<p class="soh__text">{{ spinningText }}</p>

		<p class="soh__subtitle">{{ $t('WEBAPP_PREVIOUSLY_STREAMED_ON') }}</p>

		<div class="soh__content-wrapper" aria-hidden="true">
			<table class="soh__content">
				<colgroup>
					<col class="soh__content__logo" />
					<col class="soh__content__data" />
				</colgroup>
				<thead>
					<tr>
						<th></th>
						<th>
							<div class="timeline-container">
								<div class="timeline">
									<div
										v-for="date in timelineDates"
										:key="date"
										class="timeline__point"
										:class="isJan(date) ? 'timeline__point--year' : ''"
									>
										<div class="timeline__point__display">{{ formatTimelineDate(date) }}</div>
									</div>
								</div>
							</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in renderedRows" :key="row.itemId">
						<td class="provider-cell">
							<ProviderIcon
								:iconUrl="row.iconUrl"
								:providerShortName="row.providerShortName"
								:lazy="false"
							/>
						</td>
						<td class="timeline-cell">
							<div class="data">
								<div
									v-for="point in row.dataPoints"
									:key="point.date"
									class="data__point"
									:class="point.classes"
								>
									<span class="tooltip">{{ point.tooltip }}</span>
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<section class="data-fold" aria-label="Streaming history">
			<ul>
				<template v-for="item in sortedOffersHistory">
					<li v-for="(date, index) in item.dateRanges" :key="`${item.id}_${index}_${item.package.id}`">
						<span class="provider">{{ item.package.clearName }}</span
						>: <time :datetime="date.start">{{ dateFormatter.format(new Date(date.start)) }}</time> -
						<time :datetime="date.end">{{ dateFormatter.format(new Date(date.end)) }}</time>
					</li>
				</template>
			</ul>
		</section>
	</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { OfferHistory, ObjectType } from '@/@types/graphql-types';
import { useStreamingOffersHistory } from '@/features/streaming-offers-history/composables/useStreamingOffersHistory';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';

type SlimOfferHistory = Omit<OfferHistory, 'package'> & {
	package: Partial<OfferHistory['package']>;
};

const props = defineProps<{
	titleName?: string;
	titleObjectId: number;
	titleObjectType: ObjectType;
	offersHistory: SlimOfferHistory[];
	country: string;
	language: string;
}>();

const { todayDate } = usePersistedTodaysDate();
const { dateFormatter, spinningText, sortedOffersHistory } = useStreamingOffersHistory({
	offersHistory: () => props.offersHistory,
	titleName: () => props.titleName ?? '',
});

const titleContext = computed(() => new SnowplowTitleContextGraphql(props.titleObjectId, props.titleObjectType));

const onVisibleCallback = {
	callback: (isVisible: boolean) => {
		if (isVisible) {
			TrackingHelper.trackEvent(
				'stream_offer_history_feature',
				{
					action: 'impression',
					label: 'stream_offer_history',
					nonInteraction: true,
				},
				[titleContext.value]
			);
		}
	},
};

// Get the earliest and latest dates an offer was added/removed for the title
const dateBounds = computed(() => {
	const dates = props.offersHistory
		.flatMap(({ dateRanges }) => dateRanges)
		.flatMap(({ start, end }) => [new Date(start).getTime(), new Date(end).getTime()]);

	return {
		earliestStart: new Date(Math.min(...dates)),
		latestEnd: new Date(Math.max(...dates)),
	};
});

const timelineDates = computed(() => {
	const earliestStart = dateBounds.value.earliestStart;
	const latestEnd = dateBounds.value.latestEnd;

	if (!earliestStart || !latestEnd || !isFinite(earliestStart.getTime()) || !isFinite(latestEnd.getTime())) {
		return [];
	}

	const earliestYear = earliestStart.getFullYear();
	const latestYear = latestEnd.getFullYear();

	let startYear, startMonth, endYear, endMonth;

	if (earliestYear === latestYear) {
		// If the full timespan is within the same year, set the timeline to only show that year
		startYear = earliestYear;
		startMonth = 0;
		endYear = latestYear + 1;
		endMonth = 0;
	} else {
		const currentDate = todayDate.value;
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth();
		const oneYearAfterLatest = latestYear + 1;

		startYear = earliestYear;
		startMonth = 0;

		if (currentYear < oneYearAfterLatest) {
			endYear = currentYear;
			endMonth = currentMonth;
		} else {
			endYear = oneYearAfterLatest;
			endMonth = latestEnd.getMonth();
		}
	}

	// Calculate total months
	const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;

	if (totalMonths <= 0 || totalMonths > 1200) {
		// Safety check: prevent excessive array sizes (> 100 years)
		return [];
	}

	const monthsArray = new Array(totalMonths);

	// Generate month strings
	let currentYear = startYear;
	let currentMonth = startMonth;

	for (let i = 0; i < totalMonths; i++) {
		monthsArray[i] = `${String(currentMonth + 1).padStart(2, '0')}/${currentYear}`;
		currentMonth++;
		if (currentMonth > 11) {
			currentMonth = 0;
			currentYear++;
		}
	}

	return monthsArray;
});

// Pre-render all rows with their data points to avoid nested v-for calculations
const renderedRows = computed(() => {
	const dates = timelineDates.value;
	if (dates.length === 0) return [];

	const rows = [];

	for (const item of sortedOffersHistory.value) {
		const dataPoints = [];

		for (const date of dates) {
			const [month, year] = date.split('/');
			const isoString = new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toISOString().slice(0, 7);

			let isStart = false;
			let isEnd = false;
			let isInclusive = false;
			let matchingRange: { start: string; end: string } | null = null;

			for (const range of item.dateRanges) {
				const itemDateFrom = range.start.slice(0, 7);
				const itemDateTo = range.end.slice(0, 7);

				if (itemDateFrom <= isoString && itemDateTo >= isoString) {
					if (!matchingRange) matchingRange = range;
					if (itemDateFrom === isoString) isStart = true;
					if (itemDateTo === isoString) isEnd = true;
					if (itemDateFrom < isoString && itemDateTo > isoString) isInclusive = true;
				}
			}

			const classes = {
				'data__point--start': isStart,
				'data__point--end': isEnd,
				'data__point--inclusive': isInclusive,
			};

			let tooltip = '';
			if (matchingRange) {
				const startDate = dateFormatter.format(new Date(matchingRange.start));
				const endDate = dateFormatter.format(new Date(matchingRange.end));
				tooltip = `${startDate} - ${endDate}`;
			}

			dataPoints.push({ date, classes, tooltip });
		}

		rows.push({
			itemId: item.id,
			iconUrl: item.package.iconWide,
			providerShortName: item.package.shortName,
			dataPoints,
		});
	}

	return rows;
});

function formatTimelineDate(date: string) {
	const month = date.split('/')[0];
	const year = date.split('/')[1];
	return month === '01' ? year : '';
}

function isJan(date: string) {
	return date.split('/')[0] === '01';
}
</script>

<style lang="scss" scoped>
.soh {
	margin-bottom: 16px;

	@media (max-width: 750px) {
		margin: 0 -16px;
		padding-left: 16px;
	}

	&__text {
		margin-top: 0;
	}

	&__subtitle {
		color: #d2d3d5;
		font-size: 16px;
		font-weight: 900;
		line-height: 24px;
		letter-spacing: 0.24px;
	}

	&__content-wrapper {
		width: 100%;

		@media (max-width: 750px) {
			overflow-x: scroll;
			scrollbar-color: #fff #232a32;
			padding-bottom: 8px;
		}
	}

	&__content {
		width: 100%;
		min-width: 750px;

		background-color: #101720;
		border-radius: 8px;
		padding: 16px;
		padding-top: 8px;

		&__logo {
			width: 1%;
			white-space: nowrap;
		}

		&__data {
			width: auto;
		}
	}
}

.timeline-container {
	padding: 16px;
	margin-top: 16px;
}

.timeline {
	height: 2px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: #232931;
	border-radius: 4px;

	&__point {
		position: relative;
		height: 2px;
		width: 100%;

		&::before {
			content: '';
			display: block;
			position: absolute;
			left: 0;
			top: 50%;
			transform: translateY(-50%);
			height: 2px;
			width: 2px;
			border-radius: 50%;
			background-color: #333840;
		}

		&--year {
			&::before {
				content: '';
				display: block;
				position: absolute;
				left: 0;
				top: 50%;
				transform: translateY(-50%);
				height: 6px;
				width: 6px;
				border-radius: 50%;
				background-color: #909398;
			}
		}

		&__display {
			position: absolute;
			bottom: 12px;
			transform: translateX(-50%);
			color: #d2d3d5;
			text-align: center;
			font-size: 12px;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.18px;
		}
	}
}

.data {
	height: 2px;
	background-color: #232a32;
	margin: 0 16px;
	border-radius: 4px;

	display: flex;
	justify-content: space-between;
	align-items: center;

	&__point {
		position: relative;
		height: 2px;
		width: 100%;

		&--start {
			background-color: white;
			cursor: pointer;
		}

		&--start,
		&--end {
			&::before {
				content: '';
				display: block;
				position: absolute;
				left: 0;
				top: 50%;
				transform: translateY(-50%);
				height: 6px;
				width: 6px;
				border-radius: 50%;
				background-color: white;
			}
		}

		&--inclusive {
			background: white;
			cursor: pointer;
		}

		.tooltip {
			display: none;
			position: absolute;
			bottom: 100%;
			left: 50%;
			transform: translateX(-50%) translateY(-8px);
			background: #1a2029;
			color: #d2d3d5;
			padding: 4px 8px;
			border-radius: 4px;
			white-space: nowrap;
			font-size: 12px;
			z-index: 10;
			pointer-events: none;
			opacity: 0;
			transition: opacity 0.15s;
			font-size: 14px;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.21px;
		}

		&.data__point--inclusive:hover .tooltip,
		&.data__point--start:hover .tooltip {
			display: block;
			opacity: 1;
		}
	}
}

.provider-cell,
.timeline-cell {
	padding-bottom: 8px;
}

.data-fold > ul {
	border: 0;
	clip: rect(0 0 0 0);
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	white-space: nowrap;
	width: 1px;
}
</style>
